import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
} from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Plus, RefreshCw, Save, Trash2, UploadCloud } from "@/components/icons";
import i18n from "@/i18n";
import { getPricingDraft, publishPricing, savePricingDraft } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { formatDateTime } from "@/lib/locale-format";
import {
  createEmptyPreset,
  majorInputToMinor,
  minorToMajorInput,
  normalizePricingContent,
} from "@/lib/pricing";
import type {
  GetPricingDraftResp,
  PricingContent,
  PricingPreset,
  PricingTier,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/** Commits a major-unit price on blur; reverts to the last value when invalid. */
function PriceInput({
  minor,
  currency,
  onCommit,
}: {
  minor: number;
  currency: string;
  onCommit: (minor: number) => void;
}) {
  const [text, setText] = useState(minorToMajorInput(minor));

  useEffect(() => {
    setText(minorToMajorInput(minor));
  }, [minor]);

  return (
    <div className="flex items-center gap-2">
      <Input
        inputMode="decimal"
        className="w-28 text-right"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const parsed = majorInputToMinor(text);
          if (parsed === null) {
            setText(minorToMajorInput(minor));
            return;
          }
          onCommit(parsed);
        }}
        aria-label={i18n.t("pricing.priceAria", { currency })}
      />
      <span className="text-sm text-muted-foreground">{currency}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export function PricingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PricingContent | null>(null);
  const [activeLocale, setActiveLocale] = useState("");
  const [deployError, setDeployError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const query = useQuery<GetPricingDraftResp>({
    queryKey: ["pricingDraft"],
    queryFn: getPricingDraft,
  });

  useEffect(() => {
    if (!query.data) return;
    setDraft(structuredClone(query.data.pricing));
    setActiveLocale((prev) =>
      query.data.pricing.config.locales.includes(prev)
        ? prev
        : query.data.pricing.config.defaultLocale,
    );
  }, [query.data]);

  const update = (fn: (d: PricingContent) => void) =>
    setDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      fn(next);
      return next;
    });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error(i18n.t("pricing.notLoaded"));
      return savePricingDraft({ pricing: normalizePricingContent(draft) });
    },
    onSuccess: async () => {
      toast.success(t("pricing.draftSaved"));
      await queryClient.invalidateQueries({ queryKey: ["pricingDraft"] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  // Publish always saves the current draft first so what the admin sees
  // is exactly what goes live.
  const publishMutation = useMutation({
    mutationFn: async (redeployOnly: boolean) => {
      if (redeployOnly) return publishPricing({ redeployOnly: true });
      if (!draft) throw new Error(i18n.t("pricing.notLoaded"));
      await savePricingDraft({ pricing: normalizePricingContent(draft) });
      return publishPricing({});
    },
    onSuccess: async (resp) => {
      if (resp.deployTriggered) {
        setDeployError(null);
        toast.success(
          resp.published
            ? t("pricing.publishedRebuild", { version: resp.version })
            : t("pricing.rebuildStarted"),
        );
      } else {
        setDeployError(resp.deployError ?? t("pricing.unknownDeployError"));
        toast.warning(
          resp.published
            ? t("pricing.publishedRebuildFailed", { version: resp.version })
            : t("pricing.rebuildFailed"),
        );
      }
      await queryClient.invalidateQueries({ queryKey: ["pricingDraft"] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  if (query.isError) {
    return <p className="text-sm text-destructive">{t("pricing.loadFailed")}</p>;
  }

  if (query.isLoading || !draft) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const meta = query.data;
  const busy = saveMutation.isPending || publishMutation.isPending;
  const dirty =
    meta?.isSeed || JSON.stringify(draft) !== JSON.stringify(meta?.pricing);

  const publishedLine =
    typeof meta?.publishedVersion === "number"
      ? `${t("pricing.publishedLine", { version: meta.publishedVersion })}${
          meta.publishedAt ? `, ${formatDateTime(new Date(meta.publishedAt))}` : ""
        }`
      : t("pricing.neverPublished");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("pricing.eyebrow")}
        title={t("pricing.title")}
        subtitle={publishedLine}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={busy || !dirty}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? (
                t("common.saving")
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t("pricing.saveDraft")}
                </>
              )}
            </Button>
            <Button disabled={busy} onClick={() => setPublishOpen(true)}>
              <UploadCloud className="h-4 w-4" />
              {t("pricing.publish")}
            </Button>
          </div>
        }
      />

      {meta?.isSeed && (
        <p className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
          {t("pricing.seedNote")}
        </p>
      )}

      {deployError && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3">
          <p className="text-sm">
            <span className="font-medium">{t("pricing.deployFailedLabel")}</span>{" "}
            {deployError}
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => publishMutation.mutate(true)}
          >
            <RefreshCw className="h-4 w-4" />
            {t("pricing.retryDeploy")}
          </Button>
        </div>
      )}

      <ConfigCard draft={draft} update={update} />

      <div
        className="inline-flex gap-1 rounded-md border p-1"
        role="group"
        aria-label={t("pricing.copyLanguageAria")}
      >
        {draft.config.locales.map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => setActiveLocale(locale)}
            aria-pressed={locale === activeLocale}
            className={cn(
              "rounded px-3 py-1.5 text-xs font-medium uppercase transition-colors",
              locale === activeLocale
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {locale}
          </button>
        ))}
      </div>

      {draft.tiers.map((tier, tierIndex) => (
        <TierCard
          key={tier.id || tierIndex}
          tier={tier}
          tierIndex={tierIndex}
          locale={activeLocale}
          currencies={draft.config.currencies}
          locales={draft.config.locales}
          update={update}
        />
      ))}

      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pricing.publishDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {typeof meta?.publishedVersion === "number"
                ? t("pricing.publishDialogDescReplacing", { version: meta.publishedVersion })
                : t("pricing.publishDialogDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => publishMutation.mutate(false)}>
              {t("pricing.publish")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConfigCard({
  draft,
  update,
}: {
  draft: PricingContent;
  update: (fn: (d: PricingContent) => void) => void;
}) {
  const { t } = useTranslation();
  const { config } = draft;
  const [currenciesText, setCurrenciesText] = useState(config.currencies.join(", "));

  useEffect(() => {
    setCurrenciesText(config.currencies.join(", "));
  }, [config.currencies]);

  const commitCurrencies = () => {
    const parsed = Array.from(
      new Set(
        currenciesText
          .split(",")
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean),
      ),
    );
    const first = parsed[0];
    if (!first || parsed.some((c) => !/^[A-Z]{3}$/.test(c))) {
      toast.error(t("pricing.currenciesInvalid"));
      setCurrenciesText(config.currencies.join(", "));
      return;
    }
    update((d) => {
      d.config.currencies = parsed;
      // Keep locale→currency mapping valid and make sure every tier and
      // preset has an entry per currency (new ones start at 0 and must be
      // filled in before saving).
      for (const locale of d.config.locales) {
        const current = d.config.localeCurrency[locale];
        if (!current || !parsed.includes(current)) {
          d.config.localeCurrency[locale] = first;
        }
      }
      const fix = (prices: Record<string, number>) => {
        for (const c of parsed) if (prices[c] === undefined) prices[c] = 0;
        for (const c of Object.keys(prices)) if (!parsed.includes(c)) delete prices[c];
      };
      for (const tier of d.tiers) {
        if (tier.prices) fix(tier.prices);
        for (const preset of tier.presets ?? []) fix(preset.prices);
      }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("pricing.configTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-6">
        <Field label={t("pricing.currenciesLabel")}>
          <Input
            className="w-48"
            value={currenciesText}
            onChange={(e) => setCurrenciesText(e.target.value)}
            onBlur={commitCurrencies}
            placeholder="CZK, EUR"
          />
        </Field>
        {config.locales.map((locale) => (
          <Field key={locale} label={t("pricing.currencyForLocale", { locale })}>
            <Select
              value={config.localeCurrency[locale]}
              onValueChange={(value) =>
                update((d) => {
                  d.config.localeCurrency[locale] = value;
                })
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.currencies.map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ))}
      </CardContent>
    </Card>
  );
}

function TierCard({
  tier,
  tierIndex,
  locale,
  currencies,
  locales,
  update,
}: {
  tier: PricingTier;
  tierIndex: number;
  locale: string;
  currencies: string[];
  locales: string[];
  update: (fn: (d: PricingContent) => void) => void;
}) {
  const { t } = useTranslation();
  const copy = tier.copy[locale];
  if (!copy) return null;

  const patchCopy = (patch: Partial<PricingTier["copy"][string]>) =>
    update((d) => {
      const target = d.tiers[tierIndex]?.copy[locale];
      if (target) Object.assign(target, patch);
    });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-3">
          <CardTitle className="text-base">{copy.name || tier.id}</CardTitle>
          <Badge variant="outline">{tier.id}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Label
            htmlFor={`tier-highlighted-${tierIndex}`}
            className="text-sm font-normal text-muted-foreground"
          >
            {t("pricing.highlighted")}
          </Label>
          <Switch
            id={`tier-highlighted-${tierIndex}`}
            checked={tier.highlighted}
            onCheckedChange={(checked) =>
              update((d) => {
                const target = d.tiers[tierIndex];
                if (target) target.highlighted = checked === true;
              })
            }
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("pricing.nameLabel")}>
            <Input
              value={copy.name}
              onChange={(e) => patchCopy({ name: e.target.value })}
            />
          </Field>
          <Field label={t("pricing.buttonLabel")}>
            <Input
              value={copy.cta}
              onChange={(e) => patchCopy({ cta: e.target.value })}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("pricing.taglineLabel")}>
              <Input
                value={copy.tagline}
                onChange={(e) => patchCopy({ tagline: e.target.value })}
              />
            </Field>
          </div>
          {tier.highlighted && (
            <Field label={t("pricing.badgeLabel")}>
              <Input
                value={copy.badge ?? ""}
                onChange={(e) => patchCopy({ badge: e.target.value || null })}
              />
            </Field>
          )}
          {tier.presets && (
            <Field label={t("pricing.legendLabel")}>
              <Input
                value={copy.legend ?? ""}
                onChange={(e) => patchCopy({ legend: e.target.value || null })}
              />
            </Field>
          )}
          <div className="sm:col-span-2">
            <Field label={t("pricing.featuresLabel")}>
              <Textarea
                rows={Math.max(4, copy.features.length + 1)}
                value={copy.features.join("\n")}
                onChange={(e) => patchCopy({ features: e.target.value.split("\n") })}
              />
            </Field>
          </div>
        </div>

        {tier.prices && (
          <div className="flex flex-wrap items-end gap-4">
            {currencies.map((currency) => (
              <Field key={currency} label={t("pricing.priceLabel", { currency })}>
                <PriceInput
                  minor={tier.prices?.[currency] ?? 0}
                  currency={currency}
                  onCommit={(minor) =>
                    update((d) => {
                      const prices = d.tiers[tierIndex]?.prices;
                      if (prices) prices[currency] = minor;
                    })
                  }
                />
              </Field>
            ))}
          </div>
        )}

        {tier.presets && (
          <div className="space-y-3">
            {tier.presets.map((preset, presetIndex) => (
              <PresetRow
                key={presetIndex}
                tierIndex={tierIndex}
                preset={preset}
                presetIndex={presetIndex}
                presetCount={tier.presets?.length ?? 0}
                locale={locale}
                currencies={currencies}
                update={update}
              />
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                update((d) => {
                  const presets = d.tiers[tierIndex]?.presets;
                  if (presets) presets.push(createEmptyPreset(locales, presets));
                })
              }
            >
              <Plus className="h-4 w-4" />
              {t("pricing.addPreset")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PresetRow({
  tierIndex,
  preset,
  presetIndex,
  presetCount,
  locale,
  currencies,
  update,
}: {
  tierIndex: number;
  preset: PricingPreset;
  presetIndex: number;
  presetCount: number;
  locale: string;
  currencies: string[];
  update: (fn: (d: PricingContent) => void) => void;
}) {
  const { t } = useTranslation();
  const copy = preset.copy[locale];

  const patch = (fn: (p: PricingPreset) => void) =>
    update((d) => {
      const target = d.tiers[tierIndex]?.presets?.[presetIndex];
      if (target) fn(target);
    });

  const numberInput = (
    label: string,
    value: number,
    commit: (value: number) => void,
  ) => (
    <Field label={label}>
      <Input
        type="number"
        min={1}
        className="w-24"
        value={value}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          if (Number.isInteger(parsed) && parsed > 0) commit(parsed);
        }}
      />
    </Field>
  );

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t("pricing.idLabel")}>
          <Input
            className="w-24"
            value={preset.id}
            onChange={(e) =>
              patch((p) => {
                p.id = e.target.value;
              })
            }
          />
        </Field>
        {numberInput(t("pricing.storageLabel"), preset.storageGb, (v) =>
          patch((p) => {
            p.storageGb = v;
          }),
        )}
        {numberInput(t("pricing.monthsLabel"), preset.months, (v) =>
          patch((p) => {
            p.months = v;
          }),
        )}
        {currencies.map((currency) => (
          <Field key={currency} label={t("pricing.priceLabel", { currency })}>
            <PriceInput
              minor={preset.prices[currency] ?? 0}
              currency={currency}
              onCommit={(minor) =>
                patch((p) => {
                  p.prices[currency] = minor;
                })
              }
            />
          </Field>
        ))}
        <label className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
          <input
            type="radio"
            name={`default-preset-${tierIndex}`}
            checked={preset.isDefault}
            onChange={() =>
              update((d) => {
                for (const p of d.tiers[tierIndex]?.presets ?? []) {
                  p.isDefault = false;
                }
                const target = d.tiers[tierIndex]?.presets?.[presetIndex];
                if (target) target.isDefault = true;
              })
            }
          />
          {t("pricing.preselected")}
        </label>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto text-destructive"
          disabled={presetCount <= 1}
          onClick={() =>
            update((d) => {
              const presets = d.tiers[tierIndex]?.presets;
              if (!presets) return;
              const removed = presets.splice(presetIndex, 1)[0];
              const next = presets[0];
              if (removed?.isDefault && next) next.isDefault = true;
            })
          }
          aria-label={t("pricing.removePreset")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {copy && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("pricing.pillLabel")}>
            <Input
              value={copy.pill}
              onChange={(e) =>
                patch((p) => {
                  const c = p.copy[locale];
                  if (c) c.pill = e.target.value;
                })
              }
            />
          </Field>
          <Field label={t("pricing.detailLabel")}>
            <Input
              value={copy.detail}
              onChange={(e) =>
                patch((p) => {
                  const c = p.copy[locale];
                  if (c) c.detail = e.target.value;
                })
              }
            />
          </Field>
        </div>
      )}
    </div>
  );
}
